<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto space-y-6">

      <!-- HEADER -->
      <div class="sci-page-header pb-6">
        <button
          @click="$router.back()"
          class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-4 group"
        >
          <v-icon small class="group-hover:-translate-x-1 transition-transform" color="inherit">mdi-arrow-left</v-icon>
          <span class="text-[10px] font-black uppercase tracking-widest">Voltar</span>
        </button>
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-black text-slate-800 uppercase tracking-tight">Cadastro em Lote</h1>
            <p class="text-sm text-slate-500 font-medium mt-1">Registre a situação de vários veículos de uma só vez</p>
          </div>
        </div>
      </div>

      <!-- PASSO 1: SELEÇÃO DA PREFEITURA -->
      <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-domain</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">1. SELECIONE A PREFEITURA</h2>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Prefeitura</label>
              <select
                v-model="selectedPrefecture"
                @change="loadVehicles"
                class="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
              >
                <option :value="null" disabled>Selecione a Prefeitura...</option>
                <option v-for="item in prefectures" :key="item.prefecture_id" :value="item.prefecture_id">
                  {{ item.prefecture_social_name }}
                </option>
              </select>
            </div>
            <div v-if="vehicles.length" class="flex items-end">
              <div class="w-full bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-3 flex items-center gap-3">
                <v-icon color="emerald" size="28">mdi-car-multiple</v-icon>
                <div>
                  <p class="text-[10px] font-black text-emerald-600 uppercase tracking-wider">Veículos Ativos Encontrados</p>
                  <p class="text-2xl font-black text-emerald-700">{{ vehicles.length }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- PASSO 2: APLICAR EM LOTE -->
      <section v-if="vehicles.length" class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-lightning-bolt</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">2. APLICAR A TODOS (OPCIONAL)</h2>
          <span class="ml-auto text-slate-400 text-[10px] font-bold uppercase tracking-widest">Preenche todos de uma vez — pode editar individualmente depois</span>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Data Global</label>
              <input
                type="date"
                v-model="globalDate"
                class="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
              />
            </div>
            <div>
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Situação Global</label>
              <select
                v-model="globalSituacao"
                class="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
              >
                <option :value="null" disabled>Selecione a situação...</option>
                <option :value="1">🟢 Em Uso</option>
                <option :value="2">🟡 Em Manutenção</option>
                <option :value="3">🔴 Baixada</option>
                <option :value="4">🟠 Baixa Provisória</option>
              </select>
            </div>
            <div>
              <button
                @click="applyToAll"
                :disabled="!globalDate && !globalSituacao"
                class="w-full h-[46px] bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-[11px] rounded-lg uppercase tracking-widest transition-all flex items-center justify-center gap-2"
              >
                <v-icon size="16" color="white">mdi-check-all</v-icon>
                Aplicar a Todos
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- PASSO 3: GRID DE VEÍCULOS -->
      <section v-if="vehicles.length" class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-format-list-checks</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">3. SITUAÇÃO POR VEÍCULO</h2>
          </div>
          <div class="flex items-center gap-4">
            <!-- Counter -->
            <span class="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full"
              :class="filledCount > 0 ? 'bg-orange-500 text-white' : 'bg-slate-600 text-slate-300'"
            >
              {{ filledCount }} / {{ vehicles.length }} preenchidos
            </span>
            <!-- Busca -->
            <div class="flex items-center gap-2 bg-slate-700 rounded-lg px-3 py-1.5">
              <v-icon size="14" color="white" style="opacity:0.5">mdi-magnify</v-icon>
              <input
                v-model="search"
                type="text"
                placeholder="Buscar veículo..."
                class="bg-transparent text-white text-[11px] font-bold uppercase tracking-widest outline-none placeholder:text-white/30 w-32"
              />
            </div>
          </div>
        </div>

        <!-- Cabeçalho da tabela -->
        <div class="grid grid-cols-12 gap-2 px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div class="col-span-1 text-[10px] font-black text-slate-400 uppercase tracking-wider">#</div>
          <div class="col-span-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Veículo / Máquina</div>
          <div class="col-span-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">Placa / Código</div>
          <div class="col-span-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">Data</div>
          <div class="col-span-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">Situação</div>
          <div class="col-span-1 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">Status</div>
        </div>

        <!-- Linhas dos veículos -->
        <div class="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
          <div
            v-for="(row, index) in filteredVehicles"
            :key="row.vehicle_id"
            class="grid grid-cols-12 gap-2 px-6 py-3 items-center hover:bg-slate-50/80 transition-colors"
            :class="{ 'bg-emerald-50/40': isRowFilled(row) }"
          >
            <!-- Número -->
            <div class="col-span-1">
              <span class="text-[11px] font-black text-slate-300">{{ index + 1 }}</span>
            </div>

            <!-- Nome -->
            <div class="col-span-4">
              <span class="text-xs font-bold text-slate-700 uppercase tracking-tight">{{ row.brand_desc }} {{ row.model_desc }}</span>
            </div>

            <!-- Placa / Código -->
            <div class="col-span-2">
              <span class="text-[11px] font-black text-orange-500 tracking-widest uppercase">
                {{ row.vehicle_categoria == 1 ? row.vehicle_board : row.vehicle_cod_maquina }}
              </span>
            </div>

            <!-- Data -->
            <div class="col-span-2">
              <input
                type="date"
                v-model="row._date"
                class="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all"
              />
            </div>

            <!-- Situação -->
            <div class="col-span-2">
              <select
                v-model="row._situacao"
                class="w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-[11px] font-semibold text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all"
              >
                <option :value="null">— Selecione</option>
                <option :value="1">🟢 Em Uso</option>
                <option :value="2">🟡 Em Manutenção</option>
                <option :value="3">🔴 Baixada</option>
                <option :value="4">🟠 Baixa Provisória</option>
              </select>
            </div>

            <!-- Status visual -->
            <div class="col-span-1 flex justify-center">
              <span v-if="isRowFilled(row)" class="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                <v-icon size="12" color="white">mdi-check</v-icon>
              </span>
              <span v-else class="w-6 h-6 rounded-full border-2 border-slate-200"></span>
            </div>
          </div>
        </div>

        <!-- Nenhum resultado de busca -->
        <div v-if="filteredVehicles.length === 0" class="py-16 flex flex-col items-center text-slate-300">
          <v-icon size="40">mdi-magnify-remove-outline</v-icon>
          <p class="text-[10px] font-black uppercase tracking-[0.2em] mt-3">Nenhum veículo encontrado</p>
        </div>
      </section>

      <!-- RODAPÉ DE AÇÕES -->
      <div v-if="vehicles.length" class="flex items-center justify-between py-4">
        <div class="text-sm text-slate-500">
          <span v-if="filledCount === 0">Preencha data e situação nos veículos desejados</span>
          <span v-else class="font-semibold text-slate-700">
            <span class="text-orange-500 font-black">{{ filledCount }}</span> registro(s) prontos para salvar
          </span>
        </div>
        <div class="flex items-center gap-3 flex-shrink-0">
          <button
            @click="clearAll"
            class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest px-4 py-2.5 transition-colors flex-none"
          >
            Limpar Tudo
          </button>
          <div class="flex-none">
            <button
              @click="saveBatch"
              :disabled="filledCount === 0 || loader"
              class="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all whitespace-nowrap flex items-center gap-2"
            >
              <span v-if="loader" class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              <v-icon v-else small color="white" style="font-size: 14px;">mdi-content-save-all</v-icon>
              <span>{{ loader ? 'SALVANDO...' : `SALVAR ${filledCount} REGISTRO(S)` }}</span>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snack.show" :color="snack.color" timeout="4000" top elevation="24">
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loader: false,
      prefectures: [],
      vehicles: [],
      selectedPrefecture: null,
      globalDate: null,
      globalSituacao: null,
      search: "",
      snack: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  created() {
    this.$http.get("prefectures").then((r) => (this.prefectures = r.data));

    // Se o usuário for de uma prefeitura, pré-selecionar
    const userData = this.$store.getters.getUserData;
    if (userData.table_key === "prefectures" && userData.table_id) {
      this.selectedPrefecture = userData.table_id;
      this.loadVehicles();
    }
  },
  computed: {
    filteredVehicles() {
      if (!this.search) return this.vehicles;
      const term = this.search.toLowerCase().trim();
      return this.vehicles.filter((v) => {
        const board = (v.vehicle_board || "").toLowerCase();
        const model = (v.model_desc || "").toLowerCase();
        const brand = (v.brand_desc || "").toLowerCase();
        const cod = (v.vehicle_cod_maquina || "").toLowerCase();
        return board.includes(term) || model.includes(term) || brand.includes(term) || cod.includes(term);
      });
    },
    filledCount() {
      return this.vehicles.filter((v) => v._date && v._situacao).length;
    },
  },
  methods: {
    loadVehicles() {
      if (!this.selectedPrefecture) return;
      this.vehicles = [];
      this.search = "";
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/vehicles/prefectures/${this.selectedPrefecture}`).then((r) => {
        // Só veículos ativos
        const ativos = r.data.filter((v) => v.vehicle_active === true);
        // Adiciona campos reativos de edição por linha
        this.vehicles = ativos.map((v) => ({
          ...v,
          _date: null,
          _situacao: null,
        }));
        this.$store.dispatch("setLoader", false);
      }).catch(() => {
        this.$store.dispatch("setLoader", false);
      });
    },
    applyToAll() {
      this.vehicles = this.vehicles.map((v) => ({
        ...v,
        _date: this.globalDate || v._date,
        _situacao: this.globalSituacao || v._situacao,
      }));
    },
    isRowFilled(row) {
      return !!(row._date && row._situacao);
    },
    clearAll() {
      this.globalDate = null;
      this.globalSituacao = null;
      this.vehicles = this.vehicles.map((v) => ({ ...v, _date: null, _situacao: null }));
    },
    saveBatch() {
      const records = this.vehicles
        .filter((v) => v._date && v._situacao)
        .map((v) => ({
          vehicle_id: v.vehicle_id,
          prefecture_id: this.selectedPrefecture,
          data: v._date,
          situacao: v._situacao,
        }));

      if (records.length === 0) {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Preencha data e situação em pelo menos um veículo.";
        return;
      }

      this.loader = true;
      this.$store.dispatch("setLoader", true);
      this.$http
        .post("/vehicles/situacao/batch", records)
        .then((res) => {
          this.snack.show = true;
          this.snack.color = "success";
          this.snack.message = `${res.data.inserted} situação(ões) registrada(s) com sucesso!`;
          this.loader = false;
          this.$store.dispatch("setLoader", false);
          setTimeout(() => {
            this.$router.push("/vehicles-manager/situacao");
          }, 1800);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message || "Erro ao salvar registros.";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
  },
};
</script>

<style scoped>
.sci-page-wrapper {
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 40px 20px;
}
</style>
